import {
    OnChanges,
    AfterViewChecked,
    AfterViewInit,
    Component,
    OnDestroy,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    ViewChild,
    HostListener
} from "@angular/core";

export enum BoxAlignment {
    TOP_LEFT,
    TOP_CENTER,
    TOP_RIGHT,
    BOTTOM_LEFT,
    BOTTOM_CENTER,
    BOTTOM_RIGHT,
    LEFT_TOP,
    LEFT_CENTER,
    LEFT_BOTTOM,
    RIGHT_TOP,
    RIGHT_CENTER,
    RIGHT_BOTTOM
}

@Component({
    selector: "ng-popup",
    templateUrl: "./ng-popup.component.html",
    styleUrls: ["./ng-popup.component.less"]
})
export class NgPopupComponent implements AfterViewInit, AfterViewChecked, OnChanges, OnDestroy {
    /**
     * Arrow pointer to the middle of this parent element
     */
    @Input() arrowElementRef: HTMLElement;

    /**
     * parent element which sets the boundary of this popup to preven overflow
     */
    @Input() boundaryElementRef: ElementRef;

    /**
     * Arrow pointer direction
     */
    @Input() alignment: BoxAlignment;

    @Input() minWidth = 150;
    @Input() minHeight = 150;
    @Input() width: number;
    @Input() height: number;

    @ViewChild("container") containerViewChild: ElementRef;
    @ViewChild("content") contentViewChild: ElementRef;

    @Output() clickedOutside = new EventEmitter();
    @Output() escKeyPressed = new EventEmitter();

    container: HTMLDivElement;
    contentContainer: HTMLDivElement;
    containerStyle: any = {};
    contentStyle = {
        width: "unset",
        height: "unset",
        "min-width": "unset",
        "min-heigth": "unset"
    };

    arrowStyle: any = {};
    arrowShadowStyle: any = {};
    arrowIsCentered = false;
    isTopArrow = false;
    topArrowIsCentered = false;

    private isMounted = false;

    constructor(public el: ElementRef) { }

    ngOnChanges() {
        this.applyDimensions();
        this.calculatePosition();
    }

    ngAfterViewInit(): void {
        this.container = <HTMLDivElement>this.containerViewChild.nativeElement;
        this.contentContainer = <HTMLDivElement>this.contentViewChild.nativeElement;

        this.applyDimensions();
        this.calculatePosition();

        setTimeout(() => {
            this.isMounted = true;
            this.calculatePosition(); // Check if the ref element dimensions changed
        });
    }

    ngAfterViewChecked(): void {
    }

    ngOnDestroy(): void {
    }

    @HostListener("document:click", ["$event.target"])
    public onClick(targetElement: Element) {
        if (this.isMounted) {
            if (this.isParentAlive(targetElement) && !this.el.nativeElement.contains(targetElement)) {
                this.clickedOutside.emit(null);
            }
        }
    }

    @HostListener("document:keydown", ["$event"])
    onKeyDownHandler(event: KeyboardEvent) {
        if (this.isMounted && event.key === "Escape") {
            this.escKeyPressed.emit();
        }
    }

    private isParentAlive(targetElement: Element) {
        const isHTML = (): boolean => targetElement.tagName.toLowerCase() === "html";
        while (targetElement && targetElement.parentNode && !isHTML()) {
            targetElement = targetElement.parentElement;
        }

        return targetElement && (!!targetElement.parentElement || isHTML());
    }

    private applyDimensions() {
        if (this.width) {
            this.contentStyle.width = this.width + "px";
        } else {
            this.containerStyle.width = "unset";
        }

        if (this.height) {
            this.contentStyle.height = this.height + "px";
        } else {
            this.containerStyle.height = "unset";
        }

        if (this.minWidth) {
            this.contentStyle["min-heigth"] = this.minWidth + "px";
        }

        if (this.minHeight) {
            this.contentStyle["min-heigth"] = this.minHeight + "px";
        }
    }

    private calculateTop() {
        let top = 16;
        if (this.arrowElementRef && this.arrowElementRef.offsetHeight) {
            top += this.arrowElementRef.offsetTop + this.arrowElementRef.offsetHeight;
        }

        this.containerStyle.top = top + "px";
    }

    private calculateBottom() {
        let bottomPosition = 32;
        if (this.arrowElementRef && this.arrowElementRef.offsetHeight) {
            bottomPosition += this.arrowElementRef.offsetTop + this.arrowElementRef.offsetHeight;
        }

        this.containerStyle.bottom = bottomPosition + "px";
    }

    private calculatePosition() {
        if (this.arrowElementRef && this.container) {
            switch (this.alignment) {
                case BoxAlignment.BOTTOM_RIGHT:
                    this.calculateTop();
                    this.containerStyle.right = "-8px";
                    break;

                case BoxAlignment.BOTTOM_CENTER:
                    let top = 32;
                    if (this.arrowElementRef && this.arrowElementRef.offsetHeight) {
                        top += this.arrowElementRef.offsetTop + this.arrowElementRef.offsetHeight;

                        this.containerStyle.left = ((this.arrowElementRef.offsetWidth - this.container.offsetWidth) / 2) + "px";
                    }

                    this.containerStyle.top = top + "px";
                    break;

                case BoxAlignment.BOTTOM_LEFT:
                    this.calculateTop();
                    this.containerStyle.left = "-8px";
                    break;

                case BoxAlignment.TOP_RIGHT:
                    this.isTopArrow = true;
                    this.calculateBottom();
                    this.containerStyle.right = "-8px";
                    break;

                case BoxAlignment.TOP_LEFT:
                    this.isTopArrow = true;
                    this.calculateBottom();
                    this.containerStyle.right = "-8px";
                    break;

                case BoxAlignment.TOP_CENTER:
                    let topPos = 32;
                    if (this.arrowElementRef && this.arrowElementRef.offsetHeight) {
                        topPos += this.arrowElementRef.offsetTop + this.arrowElementRef.offsetHeight;

                        this.containerStyle.left = ((this.arrowElementRef.offsetWidth - this.container.offsetWidth) / 2) + "px";
                    }

                    this.containerStyle.bottom = topPos + "px";
                    break;

                default: // Elements position driven by implementer
                // this.containerStyle
            }

            // For arrow
            this.arrowIsCentered = false;
            this.topArrowIsCentered = false;
            switch (this.alignment) {
                case BoxAlignment.BOTTOM_RIGHT:
                    const midEleRefPos = this.arrowElementRef.offsetLeft + (this.arrowElementRef.offsetWidth / 2);
                    this.arrowStyle.left = midEleRefPos + "px";
                    this.arrowShadowStyle.left = midEleRefPos + 1 + "px";
                    break;

                case BoxAlignment.BOTTOM_CENTER:
                    this.arrowIsCentered = true;
                    delete this.arrowStyle.right;
                    delete this.arrowStyle.left;
                    break;

                case BoxAlignment.BOTTOM_LEFT:
                    this.arrowStyle.left = "6px";
                    this.arrowShadowStyle.left = "7px";
                    break;

                case BoxAlignment.TOP_RIGHT:
                    this.isTopArrow = true;
                    this.arrowStyle.right = "18px";
                    this.arrowShadowStyle.right = "19px";
                    break;

                case BoxAlignment.TOP_CENTER:
                    this.topArrowIsCentered = true;
                    break;

                case BoxAlignment.TOP_LEFT:
                    this.isTopArrow = true;
                    this.arrowStyle.left = "18px";
                    this.arrowShadowStyle.left = "19px";
                    break;
            }
        }
    }
}
